import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../../../../../firebase";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";

export default function CorporateUpdateLogo() {
  const [companyLogo, setCompanyLogo] = useState("");
  const [newLogo, setNewLogo] = useState(null);
  const [previewLogo, setPreviewLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [verifyCode, setVerifyCode] = useState("");
  const [enteredCode, setEnteredCode] = useState("");
  const [showVerifyInput, setShowVerifyInput] = useState(false);
  const [companyDocId, setCompanyDocId] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyData, setCompanyData] = useState(null);
  const navigate = useNavigate();

  // Dashboard ile aynı şekilde şirket bilgisi çek
  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        setLoading(true);
        const auth = getAuth();
        const user = auth.currentUser;
        const storedData = localStorage.getItem("companyData");

        if (!user || !storedData) {
          navigate("/login_corporate");
          return;
        }

        const parsedData = JSON.parse(storedData);
        const email = parsedData?.email;
        const taxNumber = parsedData?.taxNumber;
        
        if (!email || !taxNumber) {
          setMessage({ text: "Geçersiz oturum verisi.", type: "error" });
          setLoading(false);
          return;
        }

        // Şirketi vergi numarasına göre sorgula
        const companiesRef = collection(db, "companies");
        const q = query(companiesRef, where("taxInfo.taxNumber", "==", Number(taxNumber)));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          setMessage({ text: "Şirket kaydı bulunamadı.", type: "error" });
          setLoading(false);
          return;
        }

        const companyDoc = querySnapshot.docs[0];
        const data = companyDoc.data();
        
        setCompanyDocId(companyDoc.id);
        setCompanyName(data.company_name || data.company_id || "");
        setCompanyLogo(data.company_logo || "");
        setCompanyData(data);
        
      } catch (err) {
        console.error("Şirket bilgisi çekilirken hata:", err);
        setMessage({ text: "Bir hata oluştu.", type: "error" });
      } finally {
        setLoading(false);
      }
    };
    
    fetchCompanyData();
  }, [navigate]);

  const handleSelectLogo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Dosya tipi kontrolü
    if (!file.type.startsWith("image/")) {
      setMessage({ text: "Lütfen bir görsel dosyası seçin (PNG, JPG, JPEG).", type: "error" });
      return;
    }
    
    // Dosya boyutu kontrolü (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ text: "Dosya boyutu 5MB'dan küçük olmalıdır.", type: "error" });
      return;
    }
    
    setNewLogo(file);
    setPreviewLogo(URL.createObjectURL(file));
    setMessage({ text: "", type: "" });
  };

  const handleUpdateLogo = async () => {
    if (!newLogo) {
      setMessage({ text: "Lütfen yeni bir logo seçin.", type: "error" });
      return;
    }

    if (!companyDocId || !companyName) {
      setMessage({ text: "Şirket bilgisi eksik.", type: "error" });
      return;
    }

    // Rastgele 6 haneli doğrulama kodu oluştur
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setVerifyCode(code);
    setShowVerifyInput(true);
    setMessage({ 
      text: `Doğrulama kodu oluşturuldu: ${code} - Lütfen aşağıdaki alana giriniz.`, 
      type: "info" 
    });
  };

  const handleConfirmCode = async () => {
    if (!enteredCode || enteredCode.length !== 6) {
      setMessage({ text: "Lütfen 6 haneli doğrulama kodunu girin.", type: "error" });
      return;
    }

    if (enteredCode !== verifyCode) {
      setMessage({ text: "Doğrulama kodu hatalı.", type: "error" });
      return;
    }

    if (!companyDocId || !companyName) {
      setMessage({ text: "Şirket bilgisi eksik.", type: "error" });
      return;
    }

    try {
      setUploading(true);
      setMessage({ text: "Logo güncelleniyor...", type: "info" });

      // Storage referansları - Şirket ID'sine göre path oluştur
      const logoFileName = `logo_${Date.now()}.${newLogo.name.split('.').pop()}`;
      const oldLogoRef = companyLogo ? ref(storage, companyLogo) : null;
      const newLogoRef = ref(storage, `companies/${companyDocId}/${logoFileName}`);

      // Eski logoyu sil (varsa ve yeni bir logo yükleniyorsa)
      if (oldLogoRef && companyLogo.includes("firebasestorage.googleapis.com")) {
        try {
          await deleteObject(oldLogoRef);
        } catch (deleteError) {
          console.warn("Eski logo silinemedi:", deleteError);
          // Hata olsa bile devam et
        }
      }

      // Yeni logoyu yükle
      await uploadBytes(newLogoRef, newLogo);

      // Yeni URL al
      const downloadURL = await getDownloadURL(newLogoRef);

      // Firestore'da güncelle
      const companyRef = doc(db, "companies", companyDocId);
      await updateDoc(companyRef, { 
        company_logo: downloadURL,
        logo_updated_at: new Date()
      });

      // LocalStorage'daki companyData'yı güncelle
      const updatedCompanyData = {
        ...JSON.parse(localStorage.getItem("companyData")),
        company_logo: downloadURL
      };
      localStorage.setItem("companyData", JSON.stringify(updatedCompanyData));

      // State'i güncelle
      setCompanyLogo(downloadURL);
      setPreviewLogo(null);
      setNewLogo(null);
      setShowVerifyInput(false);
      setEnteredCode("");
      setVerifyCode("");
      setMessage({ 
        text: "✅ Logo başarıyla güncellendi! Dashboard'a yönlendiriliyorsunuz...", 
        type: "success" 
      });

      // 3 saniye sonra dashboard'a yönlendir
      setTimeout(() => {
        navigate("/dashboard_corporate");
      }, 3000);

    } catch (error) {
      console.error("Logo güncelleme hatası:", error);
      
      let errorMessage = "Logo güncellenirken bir hata oluştu.";
      if (error.code === 'storage/unauthorized') {
        errorMessage = "Dosya yükleme yetkiniz bulunmuyor.";
      } else if (error.code === 'storage/canceled') {
        errorMessage = "İşlem iptal edildi.";
      } else if (error.code === 'storage/unknown') {
        errorMessage = "Bilinmeyen bir hata oluştu.";
      }
      
      setMessage({ text: errorMessage, type: "error" });
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setNewLogo(null);
    setPreviewLogo(null);
    setShowVerifyInput(false);
    setEnteredCode("");
    setVerifyCode("");
    setMessage({ text: "", type: "" });
  };

  const handleBackToDashboard = () => {
    navigate("/dashboard_corporate");
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Şirket bilgileri yükleniyor...</p>
      </div>
    );
  }

  return (
    <div style={styles.mainContainer}>
      <div style={styles.container}>
        {/* Üst Bilgi */}
        <div style={styles.header}>
          <div style={styles.headerInfo}>
            <h2 style={styles.title}>Logo Güncelle</h2>
            <p style={styles.subtitle}>
              {companyName ? `${companyName}` : "Şirket"}
            </p>
            {companyData?.taxInfo?.taxNumber && (
              <div style={styles.taxInfo}>
                Vergi No: {companyData.taxInfo.taxNumber}
              </div>
            )}
          </div>
          <button 
            style={styles.backButton}
            onClick={handleBackToDashboard}
            disabled={uploading}
          >
            ← Dashboard
          </button>
        </div>

        {/* Logo Görüntüleme */}
        <div style={styles.logoSection}>
          <h3 style={styles.sectionTitle}>Mevcut Logo</h3>
          <div style={styles.logoContainer}>
            {companyLogo ? (
              <img
                src={companyLogo}
                alt="Mevcut Logo"
                style={styles.logoPreview}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'%3E%3Crect width='120' height='120' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%239ca3af' font-size='12'%3ELogo Yok%3C/text%3E%3C/svg%3E";
                }}
              />
            ) : (
              <div style={styles.noLogo}>
                <div style={styles.noLogoIcon}>🏢</div>
                <p style={styles.noLogoText}>Logo bulunamadı</p>
              </div>
            )}
          </div>
        </div>

        {/* Yeni Logo Seçme */}
        <div style={styles.uploadSection}>
          <h3 style={styles.sectionTitle}>Yeni Logo Yükle</h3>
          <div style={styles.uploadContainer}>
            <input
              type="file"
              id="logo-upload"
              accept="image/png, image/jpeg, image/jpg, image/gif"
              onChange={handleSelectLogo}
              style={styles.fileInput}
              disabled={uploading || showVerifyInput}
            />
            <label htmlFor="logo-upload" style={styles.fileInputLabel}>
              <span style={styles.uploadIcon}>📁</span>
              Logo Seç
              <span style={styles.fileInfo}>(PNG, JPG, JPEG, max 5MB)</span>
            </label>
          </div>

          {/* Önizleme */}
          {previewLogo && (
            <div style={styles.previewContainer}>
              <h4 style={styles.previewTitle}>Yeni Logo Önizlemesi</h4>
              <div style={styles.previewImageContainer}>
                <img src={previewLogo} alt="Yeni Logo Önizleme" style={styles.previewImage} />
                <div style={styles.previewActions}>
                  <button 
                    onClick={handleCancel}
                    style={styles.cancelButton}
                    disabled={uploading}
                  >
                    İptal
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Güncelleme Butonu */}
          {!showVerifyInput && newLogo && (
            <div style={styles.actionButtons}>
              <button
                onClick={handleUpdateLogo}
                disabled={uploading}
                style={{
                  ...styles.updateButton,
                  opacity: uploading ? 0.7 : 1,
                  cursor: uploading ? "not-allowed" : "pointer",
                }}
              >
                {uploading ? "Hazırlanıyor..." : "✅ Logoyu Değiştir"}
              </button>
            </div>
          )}

          {/* Doğrulama Kodu */}
          {showVerifyInput && (
            <div style={styles.verifyContainer}>
              <div style={styles.verifyHeader}>
                <h4 style={styles.verifyTitle}>Doğrulama Kodu</h4>
                <p style={styles.verifySubtitle}>
                  Güvenlik için doğrulama kodunu girin
                </p>
              </div>
              
              <div style={styles.codeInputContainer}>
                <input
                  type="text"
                  value={enteredCode}
                  onChange={(e) => setEnteredCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  style={styles.codeInput}
                  maxLength={6}
                  placeholder="000000"
                  disabled={uploading}
                  autoFocus
                />
                <div style={styles.codeHint}>
                  6 haneli kod: <strong style={styles.codeDisplay}>{verifyCode}</strong>
                </div>
              </div>

              <div style={styles.verifyActions}>
                <button
                  onClick={handleConfirmCode}
                  disabled={uploading || enteredCode.length !== 6}
                  style={{
                    ...styles.confirmButton,
                    opacity: (uploading || enteredCode.length !== 6) ? 0.7 : 1,
                    cursor: (uploading || enteredCode.length !== 6) ? "not-allowed" : "pointer",
                  }}
                >
                  {uploading ? (
                    <>
                      <div style={styles.buttonSpinner}></div>
                      Güncelleniyor...
                    </>
                  ) : "Onayla ve Güncelle"}
                </button>
                
                <button
                  onClick={handleCancel}
                  style={styles.cancelVerifyButton}
                  disabled={uploading}
                >
                  İptal
                </button>
              </div>
            </div>
          )}

          {/* Mesaj Gösterimi */}
          {message.text && (
            <div style={{
              ...styles.message,
              backgroundColor: message.type === "error" ? "#fef2f2" : 
                               message.type === "success" ? "#f0fdf4" : "#eff6ff",
              borderColor: message.type === "error" ? "#fecaca" : 
                          message.type === "success" ? "#86efac" : "#dbeafe",
              color: message.type === "error" ? "#dc2626" : 
                    message.type === "success" ? "#047857" : "#1e40af",
            }}>
              {message.text}
            </div>
          )}

          {/* Bilgilendirme */}
          <div style={styles.infoBox}>
            <div style={styles.infoIcon}>ℹ️</div>
            <div style={styles.infoContent}>
              <h4 style={styles.infoTitle}>Logo Güncelleme Kuralları</h4>
              <ul style={styles.infoList}>
                <li>PNG, JPG veya JPEG formatında olmalı</li>
                <li>Maksimum dosya boyutu: 5MB</li>
                <li>Önerilen boyut: 500x500 piksel</li>
                <li>Şeffaf arka plan için PNG kullanın</li>
                <li>Logo değişikliği güvenlik için doğrulama gerektirir</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    maxWidth: "800px",
    margin: "20px auto",
    padding: "0 16px",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "300px",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #0b63d6",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: "16px",
  },
  loadingText: {
    fontSize: "14px",
    color: "#666",
  },
  container: {
    padding: "24px",
    backgroundColor: "#f7f9fb",
    borderRadius: "12px",
    boxShadow: "0 8px 30px rgba(8,30,70,0.04)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "24px",
    paddingBottom: "20px",
    borderBottom: "1px solid #e5e7eb",
    flexWrap: "wrap",
    gap: "16px",
  },
  headerInfo: {
    flex: 1,
  },
  title: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#0b2b5a",
    marginBottom: "8px",
  },
  subtitle: {
    fontSize: "16px",
    color: "#4b5563",
    fontWeight: "500",
    marginBottom: "4px",
  },
  taxInfo: {
    fontSize: "14px",
    color: "#6b7280",
  },
  backButton: {
    backgroundColor: "#6b7280",
    color: "white",
    border: "none",
    padding: "8px 16px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "background-color 0.2s",
    whiteSpace: "nowrap",
  },
  logoSection: {
    marginBottom: "32px",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "16px",
    paddingBottom: "8px",
    borderBottom: "2px solid #e5e7eb",
  },
  logoContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "150px",
  },
  logoPreview: {
    width: "150px",
    height: "150px",
    borderRadius: "12px",
    objectFit: "contain",
    border: "3px solid #d1d5db",
    padding: "8px",
    backgroundColor: "white",
  },
  noLogo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "150px",
    height: "150px",
    borderRadius: "12px",
    backgroundColor: "#f3f4f6",
    border: "2px dashed #d1d5db",
  },
  noLogoIcon: {
    fontSize: "40px",
    marginBottom: "12px",
    color: "#9ca3af",
  },
  noLogoText: {
    fontSize: "14px",
    color: "#6b7280",
    textAlign: "center",
  },
  uploadSection: {
    marginTop: "32px",
  },
  uploadContainer: {
    marginBottom: "20px",
  },
  fileInput: {
    display: "none",
  },
  fileInputLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    padding: "12px 24px",
    backgroundColor: "white",
    border: "2px dashed #3b82f6",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    fontSize: "15px",
    fontWeight: "500",
    color: "#3b82f6",
    "&:hover": {
      backgroundColor: "#eff6ff",
    },
  },
  uploadIcon: {
    fontSize: "20px",
  },
  fileInfo: {
    fontSize: "12px",
    color: "#6b7280",
    marginLeft: "8px",
  },
  previewContainer: {
    marginTop: "24px",
    padding: "20px",
    backgroundColor: "white",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
  },
  previewTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "16px",
  },
  previewImageContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
  },
  previewImage: {
    width: "120px",
    height: "120px",
    borderRadius: "8px",
    objectFit: "contain",
    border: "2px solid #e5e7eb",
  },
  previewActions: {
    display: "flex",
    gap: "12px",
  },
  cancelButton: {
    padding: "8px 16px",
    backgroundColor: "#f3f4f6",
    color: "#374151",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  actionButtons: {
    display: "flex",
    justifyContent: "center",
    marginTop: "24px",
  },
  updateButton: {
    padding: "12px 32px",
    backgroundColor: "#0b63d6",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  verifyContainer: {
    marginTop: "32px",
    padding: "24px",
    backgroundColor: "white",
    borderRadius: "12px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  },
  verifyHeader: {
    marginBottom: "20px",
    textAlign: "center",
  },
  verifyTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "8px",
  },
  verifySubtitle: {
    fontSize: "14px",
    color: "#6b7280",
  },
  codeInputContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    marginBottom: "24px",
  },
  codeInput: {
    width: "200px",
    padding: "12px 16px",
    fontSize: "24px",
    fontWeight: "700",
    textAlign: "center",
    letterSpacing: "8px",
    border: "2px solid #3b82f6",
    borderRadius: "8px",
    outline: "none",
    color: "#1e293b",
    backgroundColor: "#f8fafc",
  },
  codeHint: {
    fontSize: "14px",
    color: "#6b7280",
  },
  codeDisplay: {
    color: "#0b63d6",
    backgroundColor: "#eff6ff",
    padding: "4px 8px",
    borderRadius: "4px",
    fontFamily: "monospace",
  },
  verifyActions: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  confirmButton: {
    padding: "14px",
    backgroundColor: "#10b981",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
  },
  cancelVerifyButton: {
    padding: "14px",
    backgroundColor: "#f3f4f6",
    color: "#374151",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "500",
    cursor: "pointer",
  },
  buttonSpinner: {
    width: "20px",
    height: "20px",
    border: "2px solid rgba(255,255,255,0.3)",
    borderTop: "2px solid white",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  message: {
    padding: "16px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "500",
    marginTop: "20px",
    border: "1px solid",
  },
  infoBox: {
    display: "flex",
    gap: "16px",
    padding: "20px",
    backgroundColor: "#f8fafc",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
    marginTop: "32px",
  },
  infoIcon: {
    fontSize: "24px",
    color: "#6b7280",
    flexShrink: 0,
    marginTop: "4px",
  },
  infoContent: {
    flex: 1,
  },
  infoTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "12px",
  },
  infoList: {
    margin: 0,
    paddingLeft: "20px",
    fontSize: "14px",
    color: "#6b7280",
    lineHeight: "1.8",
  },
};

// Spinner animation için inline CSS
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);